import {
  useGetAllDirectorsInCompany,
  useSupportedCorporations,
} from '@commenda/api';
import { IncorporationRequestDataType } from '@commenda/types';
import { EditIcon01, PencilLight, ProceedIcon } from '@commenda/icons';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
} from '@commenda/kakashi';
import { DirectorBase } from '@commenda/types/src/incorporation/types';
import { TSetNotification } from '@commenda/types/src/random';
import { WorkRequestResponseType } from '@commenda/types/src/work-request/types';
import { fetcher } from '@commenda/utils';
import { Currency, IncorporationRequestStatus } from '@prisma/client';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { IncorporationFormHeading, SubmitWorkRequestModalKS } from '../common';
import CorporateShareholdersSummary from '../common/CorporateShareholdersSummary';
import { extractCorporateShareholders } from '../common/corporateStakeholder/utils';
import CapTable from '../EquityInfo/CapTable';
import { CompanyValuationSummary } from '../EquityInfo/CompanyValuationSummary';
import { useEquityFormState } from '../EquityInfo/hooks/useEquityFormState';
import { useEquityResources } from '../EquityInfo/hooks/useEquityResources';
import SGDirectorSummaryField from './SGDirectorSummaryField';
import { SummaryCompanyInfo } from './SummaryCompanyInfo';

const SummarySection = ({
  setActiveStep,
  onProceed,
  data,
  hideActionButtons = false,
  setNotification,
  token,
  corporationId,
  companyId,
}: {
  companyId: number;
  setActiveStep: (stepIdx: number) => void;
  onProceed: (idx: number) => void;
  data: WorkRequestResponseType & { requestData: IncorporationRequestDataType };
  hideActionButtons?: boolean;
  token: string;
  setNotification: TSetNotification;
  corporationId: number;
}) => {
  const router = useRouter();

  const { id } = router.query as { id: string };

  const { data: companyData, mutate: companyMutate } =
    useGetAllDirectorsInCompany(companyId);

  const [isSubmitting, setSubmitting] = useState(false);

  const { data: corporationMetaData } = useSupportedCorporations();

  const [directors, setDirector] = useState<DirectorBase[]>([]);

  const {
    directorsData,
    corporations,
    capTableTransactions,
    stockClasses,
    mutateCorporations,
  } = useEquityResources(data?.companyId, corporationId);

  const { capTableData, equityInfo } = useEquityFormState({
    capTableTransactions,
    stockClasses,
    directorsData: directorsData?.directors,
    corporations,
    mutateCorporations,
    resetEditModalPresetState: undefined,
    corporationId,
    config: {
      defaultShareCount: 10_000_000,
      defaultSharePrice: 0.00001,
      currency: Currency.SGD,
    },
    onSubmitSuccess: undefined,
    validateStakeholdersAndGetIdsWithError: undefined,
  });

  // Get individual shareholders from cap table transactions
  const individualShareholders = useMemo(() => {
    if (!capTableTransactions || !directorsData?.directors) return [];

    // Get director IDs from cap table transactions
    const directorIdsFromCapTable = capTableTransactions
      .map((tx) => {
        const director = tx.stakeholding?.stakeholder?.director;
        if (!director) return null; // skip corporate shareholders
        return director.id;
      })
      .filter(Boolean);

    // Get full director data from directorsData
    const shareholders = directorsData.directors.filter((director) =>
      directorIdsFromCapTable.includes(director.id)
    );

    return shareholders;
  }, [capTableTransactions, directorsData]);

  const shareholderDirectorIds = useMemo(
    () => new Set(individualShareholders.map((shareholder) => shareholder.id)),
    [individualShareholders]
  );

  useEffect(() => {
    if (companyId) {
      companyMutate();
    }
  }, [companyId]);

  useEffect(() => {
    const peopleOfCorp = companyData?.directors?.filter((dir) =>
      dir.WorkRequests.some((wr) => wr.id === id)
    );

    const dirs = peopleOfCorp?.filter((dir) => {
      return dir.corporationHasDirector?.find(
        (chd) =>
          chd.corporationId === corporationId &&
          chd.directorId === dir.id &&
          chd.additionalDirectorInformation.isDirector
      );
    });

    setDirector(dirs);
  }, [companyData]);

  const currentCorporationDetails =
    corporationMetaData?.corporations?.[
      data?.corporation?.incorporationCountry
    ]?.[data?.corporation?.jurisdiction]?.[data?.corporation?.corporationType];

  const onSubmit = async () => {
    setSubmitting(true);

    const response = await fetcher(
      `workRequest/${id}`,
      {
        method: 'POST',
        body: {
          status: IncorporationRequestStatus.Submitted,
        },
      },
      token
    );

    if (!response.ok) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message:
          'Something went wrong, please try again or contact Team Commenda if you need help.',
      });
      setSubmitting(false);
      return;
    }

    setSubmitting(false);
    onProceed(3);
    router.push('/');
  };

  return (
    <div className="flex flex-col gap-8 max-w-[700px]">
      <IncorporationFormHeading
        text="Summary"
        additionalText="Please make sure the following information is correct."
      />
      <SummaryCompanyInfo
        data={data}
        setActiveStep={setActiveStep}
        currentCorporationDetails={currentCorporationDetails}
        hideActionButtons={hideActionButtons}
      />

      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h4
            className="body-16-bold text-text-primary
              "
          >
            Designated partners
          </h4>
          {hideActionButtons || directors?.length === 0 ? null : (
            <PencilLight
              className="cursor-pointer size-5"
              onClick={() => setActiveStep(1)}
            />
          )}
        </div>
        <div className="flex flex-col gap-3 rounded-lg">
          <Accordion
            className="AccordionRoot flex flex-col gap-3"
            type="single"
            collapsible
          >
            {directors?.map((dir) => (
              <AccordionItem
                className="AccordionItem rounded-md bg-bg-disabled-subtle"
                value={dir.id?.toString()}
                key={dir.id}
              >
                <AccordionTrigger className="w-full ">
                  <div className="py-3 px-6 flex justify-between items-center w-full text-text-placeholder">
                    <span className="text-base ">
                      {dir?.directorFirstName} {dir?.directorLastName}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className=" p-4 pt-0">
                  <SGDirectorSummaryField
                    key={dir.id}
                    director={dir}
                    data={data}
                    corporationId={corporationId}
                    shareholderDirectorIds={shareholderDirectorIds}
                  />
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>

      {individualShareholders && individualShareholders.length > 0 && (
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <h4
              className="body-16-bold text-text-primary
                "
            >
              Individual Shareholders
            </h4>
            {hideActionButtons ? null : (
              <EditIcon01
                className="cursor-pointer"
                onClick={() => setActiveStep(2)}
              />
            )}
          </div>
          <div className="flex flex-col gap-3 rounded-sm">
            <Accordion
              className="AccordionRoot flex flex-col gap-3"
              type="single"
              collapsible
            >
              {individualShareholders?.map((dir) => (
                <AccordionItem
                  className="AccordionItem secondary rounded-md bg-bg-disabled-subtle"
                  value={dir.id?.toString()}
                  key={dir.id}
                >
                  <AccordionTrigger className="w-full">
                    <div className="py-3 px-6 flex justify-between items-center w-full text-text-placeholder ">
                      <span className="text-base ">
                        {dir?.directorFirstName} {dir?.directorLastName}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className=" p-4 pt-0">
                    <SGDirectorSummaryField
                      key={dir.id}
                      director={dir}
                      data={data}
                      corporationId={corporationId}
                      shareholderDirectorIds={shareholderDirectorIds}
                    />
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      )}

      {extractCorporateShareholders(capTableTransactions).length > 0 && (
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <h4 className="body-16-bold text-text-primary">
              Corporate Shareholders
            </h4>
            {hideActionButtons ? null : (
              <EditIcon01
                className="cursor-pointer"
                onClick={() => setActiveStep(2)}
              />
            )}
          </div>
          <CorporateShareholdersSummary
            corporateShareholders={extractCorporateShareholders(
              capTableTransactions
            )}
            directorsData={directorsData?.directors}
          />
        </div>
      )}

      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h4 className="body-16-bold text-text-primary">Ownership Summary</h4>
        </div>
        <CompanyValuationSummary
          numberOfShares={equityInfo.shareCount}
          sharePrice={equityInfo.price}
          currency={{
            countryCode: 'en-SG',
            name: 'SGD',
            style: 'S$',
          }}
          helpTexts={{
            mainHeading: 'Paid Up Capital',
          }}
        />
        {capTableData && capTableData.length > 0 ? (
          <CapTable data={capTableData} />
        ) : (
          <p className="text-text-secondary mt-4">
            No ownership data available.
          </p>
        )}
      </div>

      {hideActionButtons ? (
        <Button className="w-fit" onClick={() => router.push('/')}>
          <ProceedIcon className="rotate-180 mr-2 h-4 w-4" />
          <span>Back to Home</span>
        </Button>
      ) : (
        <div className="flex gap-4">
          <Button variant="secondary" onClick={() => setActiveStep(2)}>
            <ProceedIcon className="rotate-180 mr-2 h-4 w-4" />
            <span>Back</span>
          </Button>
          <SubmitWorkRequestModalKS
            isSubmitting={isSubmitting}
            description="This action is irreversible. Once submitted, the details will be
                  locked for filing. Please review before submitting."
            onSubmit={onSubmit}
          />
        </div>
      )}
    </div>
  );
};

export default SummarySection;
